import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import { Button } from '../../../../../components/ui/button';
import {
	Box,
	Checkbox,
	FormControlLabel,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	CircularProgress,
	Autocomplete,
} from '@mui/material';
import * as Yup from 'yup';
import styles from './CreatePartnerSettings.module.scss';
import { CommonPartner } from 'types';
import {
	getCommonPartners,
	createPartner,
	getPartnerTypes,
	PartnerType,
	createPartnerListWorkplace,
} from 'core/API/partners';
import { GetAllWorkplaceList } from 'core/API/workplaces';

interface CreatePartnerSettingsProps {
	backButtonClick: () => void;
	onUserCreated?: () => void;
}

interface Workplace {
	id: number;
	name: string;
	level: number;
	selected: boolean;
	'@id'?: string;
}

const CreatePartnerSettings: React.FC<CreatePartnerSettingsProps> = ({
	backButtonClick,
	onUserCreated,
}) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [apiSuccess, setApiSuccess] = useState<string | null>(null);
	const [activateDialogOpen, setActivateDialogOpen] = useState(false);
	const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
	const [workplaceUnassignDialogOpen, setWorkplaceUnassignDialogOpen] = useState(false);
	const [pendingWorkplaceId, setPendingWorkplaceId] = useState<number | null>(null);
	const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
	const [loadingWorkplaces, setLoadingWorkplaces] = useState(false);
	const [commonPartners, setCommonPartners] = useState<CommonPartner[]>([]);
	const [loadingCommonPartners, setLoadingCommonPartners] = useState(false);
	const [partnerTypes, setPartnerTypes] = useState<PartnerType[]>([]);
	const [loadingPartnerTypes, setLoadingPartnerTypes] = useState(false);
	const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
	// Fetch common partners, partner types, and workplaces on mount
	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoadingCommonPartners(true);
				setLoadingPartnerTypes(true);
				setLoadingWorkplaces(true);

				const [partners, types, workplacesData] = await Promise.all([
					getCommonPartners(),
					getPartnerTypes(),
					GetAllWorkplaceList(),
				]);
				console.log('workplaces', workplacesData);
				setCommonPartners(partners.filter((p) => p.available));
				setPartnerTypes(types);
				setWorkplaces(
					(Object.values(workplacesData) || []).map((w) => ({
						id: w.id,
						name: w.name,
						level: 1, // You can enhance this with hierarchy logic if needed
						selected: false,
						'@id': `/api/workplaces/${w.id}`,
					}))
				);
			} catch (error: any) {
				console.error('Failed to fetch data:', error);
				setApiError('Failed to load options');
			} finally {
				setLoadingCommonPartners(false);
				setLoadingPartnerTypes(false);
				setLoadingWorkplaces(false);
			}
		};
		fetchData();
	}, []);

	const initialFormikValues = {
		label: '',
		partnerId: '',
		partnerTypeId: '',
		active: true,
	};

	const validationSchema = Yup.object().shape({
		label: Yup.string().required('Partner name is required'),
		partnerId: Yup.string().required('Partner is required'),
		partnerTypeId: Yup.string().required('Partner type is required'),
		active: Yup.boolean(),
	});

	const formik = useFormik({
		initialValues: initialFormikValues,
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				setApiError(null);
				setApiSuccess(null);

				const selectedPartner = commonPartners.find((p) => p.id === Number(values.partnerId));
				const selectedPartnerType = partnerTypes.find(
					(pt) => pt.id === Number(values.partnerTypeId)
				);

				if (!selectedPartner) {
					setApiError('Please select a partner');
					return;
				}

				if (!selectedPartnerType) {
					setApiError('Please select a partner type');
					return;
				}

				// Prepare the payload for creating a new partner_list
				// Build parameters object with user-entered values
				const parametersObject =
					Object.keys(parameterValues).length > 0
						? parameterValues
						: (Array.isArray(selectedPartner.parameters) && selectedPartner.parameters.length > 0)
							? selectedPartner.parameters[0]
							: {};

				const payload = {
					label: values.label,
					partnerId: selectedPartner.id,
					type: selectedPartnerType['@id'],
					parameters: parametersObject,
					active: values.active,
					deleted: false,
				};

				const createdPartner = await createPartner(payload);

				// Create workplace associations for selected workplaces
				const selectedWorkplaces = workplaces.filter((w) => w.selected);
				if (selectedWorkplaces.length > 0 && createdPartner['@id']) {
					await Promise.all(
						selectedWorkplaces.map((workplace) =>
							createPartnerListWorkplace({
								partnerList: createdPartner['@id'] || '',
								workplace: workplace['@id'] || `/api/workplaces/${workplace.id}`,
							})
						)
					);
				}

				setApiSuccess('Partner created successfully!');

				onUserCreated?.();

				setTimeout(() => {
					backButtonClick();
				}, 1500);
			} catch (error: any) {
				setApiError(error.message || 'Failed to create partner');
			} finally {
				setSubmitting(false);
			}
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		formik.handleSubmit();
	};

	const handleActivateToggle = () => {
		if (!formik.values.active) {
			setActivateDialogOpen(true);
		} else {
			setDeactivateDialogOpen(true);
		}
	};

	const confirmActivatePartner = () => {
		formik.setFieldValue('active', true);
		setActivateDialogOpen(false);
		setWorkplaces((prev) =>
			prev.map((workplace) =>
				workplace.level === 1 ? { ...workplace, selected: true } : workplace
			)
		);
		setApiSuccess('Partner activated successfully! Organization workplace automatically assigned.');
		setTimeout(() => setApiSuccess(null), 3000);
	};

	const confirmDeactivatePartner = () => {
		formik.setFieldValue('active', false);
		setDeactivateDialogOpen(false);
		setWorkplaces((prev) => prev.map((workplace) => ({ ...workplace, selected: false })));
		setApiSuccess('Partner deactivated successfully! All workplace assignments removed.');
		setTimeout(() => setApiSuccess(null), 3000);
	};

	const handleWorkplaceToggle = (workplaceId: number) => {
		const workplace = workplaces.find((w) => w.id === workplaceId);
		if (workplace && workplace.selected) {
			setPendingWorkplaceId(workplaceId);
			setWorkplaceUnassignDialogOpen(true);
		} else {
			toggleWorkplaceAssignment(workplaceId);
		}
	};

	const toggleWorkplaceAssignment = (workplaceId: number) => {
		setWorkplaces((prev) =>
			prev.map((workplace) =>
				workplace.id === workplaceId ? { ...workplace, selected: !workplace.selected } : workplace
			)
		);
	};

	const handleSelectAllWorkplaces = (checked: boolean) => {
		setWorkplaces((prev) =>
			prev.map((workplace) => ({ ...workplace, selected: checked }))
		);
	};

	const allWorkplacesSelected = workplaces.length > 0 && workplaces.every((w) => w.selected);
	const someWorkplacesSelected = workplaces.some((w) => w.selected) && !allWorkplacesSelected;

	const confirmWorkplaceUnassign = () => {
		if (pendingWorkplaceId) {
			toggleWorkplaceAssignment(pendingWorkplaceId);
			const workplace = workplaces.find((w) => w.id === pendingWorkplaceId);
			setApiSuccess(`${workplace?.name} unassigned successfully!`);
			setTimeout(() => setApiSuccess(null), 2000);
		}
		setWorkplaceUnassignDialogOpen(false);
		setPendingWorkplaceId(null);
	};

	return (
		<div className={styles.createPartnerSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={styles.backButton} onClick={backButtonClick}>
							Back
						</Button>
					</Grid>
					<Grid item xs={6} display={'flex'} justifyContent="flex-end">
						<Grid>
							<Button
								className={styles.saveButton}
								onClick={handleSubmit}
								disabled={formik.isSubmitting}
							>
								{formik.isSubmitting ? 'Saving...' : 'Save'}
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Create New Partner</h2>
				</Grid>
			</Grid>

			{/* API Feedback */}
			{apiSuccess && <div className={styles.successMessage}>{apiSuccess}</div>}
			{apiError && <div className={styles.errorMessage}>{apiError}</div>}

			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* Partner Selection */}
				<Autocomplete
					options={commonPartners}
					getOptionLabel={(option) => option.label}
					loading={loadingCommonPartners}
					value={commonPartners.find((p) => p.id === Number(formik.values.partnerId)) || null}
					onChange={(_, newValue) => {
						formik.setFieldValue('partnerId', newValue?.id || '');					// Auto-fill label with the selected partner's label
					formik.setFieldValue('label', newValue?.label || '');						// Initialize parameter values from the selected partner's parameters
						console.log('parameters newValue', newValue);
						if (newValue?.parameters) {
							const initialParams: Record<string, string> = {};
							Object.keys(newValue.parameters || []).forEach((param: any) => {
								// Extract keys from parameter objects
								initialParams[param] = '';
							});
							console.log('initialParams', initialParams);
							setParameterValues(initialParams);
						} else {
							setParameterValues({});
						}
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Partner *"
							margin="normal"
							error={formik.touched.partnerId && Boolean(formik.errors.partnerId)}
							helperText={formik.touched.partnerId && formik.errors.partnerId}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loadingCommonPartners ? <CircularProgress color="inherit" size={20} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>

				{/* Partner Type Autocomplete */}
				<Autocomplete
					options={partnerTypes}
					getOptionLabel={(option) => option.label}
					loading={loadingPartnerTypes}
					value={partnerTypes.find((pt) => pt.id === Number(formik.values.partnerTypeId)) || null}
					onChange={(_, newValue) => {
						formik.setFieldValue('partnerTypeId', newValue?.id || '');
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Partner Type *"
							margin="normal"
							error={formik.touched.partnerTypeId && Boolean(formik.errors.partnerTypeId)}
							helperText={formik.touched.partnerTypeId && formik.errors.partnerTypeId}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loadingPartnerTypes ? <CircularProgress color="inherit" size={20} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>

				{/* Dynamic Parameter Fields */}
				{Object.keys(parameterValues).length > 0 && (
					<Box
						sx={{
							marginY: 2,
							padding: 2,
							border: '1px solid rgba(0, 0, 0, 0.12)',
							borderRadius: 1,
						}}
					>
						<Box
							sx={{
								fontWeight: 400,
								fontSize: '16px',
								color: 'rgb(0 0 0 / 87%)',
								marginBottom: 2,
							}}
						>
							Partner Configuration Parameters
						</Box>
						{Object.keys(parameterValues).map((key) => (
							<TextField
								key={key}
								label={key}
								value={parameterValues[key]}
								onChange={(e) => {
									setParameterValues((prev) => ({
										...prev,
										[key]: e.target.value,
									}));
								}}
								margin="normal"
								fullWidth
							/>
						))}
					</Box>
				)}

				{/* Partner Status */}
				<Box
					sx={{
						marginY: 2,
					}}
				>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.active}
								onChange={handleActivateToggle}
								sx={{
									color: '#446388',
									'&.Mui-checked': {
										color: '#446388',
									},
								}}
							/>
						}
						label={'Active Partner'}
					/>
				</Box>

				{/* Workplace Assignment - Only show if partner is active */}
				{formik.values.active && (
					<Box
						sx={{
							border: '1px solid #ccc',
							borderRadius: 2,
							padding: 2,
							marginY: 2,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								marginBottom: 2,
							}}
						>
							<Box
								sx={{
									fontWeight: 400,
									fontSize: '16px',
									color: 'rgb(0 0 0 / 87%)',
								}}
							>
								Workplace Assignment
							</Box>
							{!loadingWorkplaces && workplaces.length > 0 && (
								<FormControlLabel
									control={
										<Checkbox
											checked={allWorkplacesSelected}
											indeterminate={someWorkplacesSelected}
											onChange={(e) => handleSelectAllWorkplaces(e.target.checked)}
											sx={{
												color: '#446388',
												'&.Mui-checked': {
													color: '#446388',
												},
												'&.MuiCheckbox-indeterminate': {
													color: '#446388',
												},
											}}
										/>
									}
									label="Select All"
								/>
							)}
						</Box>
						{loadingWorkplaces ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
								<CircularProgress size={24} color="inherit" />
							</Box>
						) : (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
								{workplaces.map((workplace) => (
									<Box
										key={workplace.id}
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											padding: '4px 8px',
											borderRadius: 1,
											'&:hover': {
												backgroundColor: 'rgba(0, 0, 0, 0.04)',
											},
										}}
									>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<span style={{ marginLeft: workplace.level * 20 }}>
												{workplace.level > 1 && '└─ '}
												{workplace.name}
											</span>
											{/* {workplace.selected && <Chip label="Assigned" size="small" color="primary" />} */}
										</Box>
										<Checkbox
											checked={workplace.selected}
											onChange={() => handleWorkplaceToggle(workplace.id)}
											sx={{
												color: '#446388',
												'&.Mui-checked': {
													color: '#446388',
												},
											}}
										/>
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}
			</form>

			{/* Partner Activation Confirmation Dialog */}
			<Dialog
				open={activateDialogOpen}
				onClose={() => setActivateDialogOpen(false)}
				aria-labelledby="activate-dialog-title"
				aria-describedby="activate-dialog-description"
			>
				<DialogTitle id="activate-dialog-title">Activate Partner</DialogTitle>
				<DialogContent>
					<DialogContentText id="activate-dialog-description">
						Are you sure you want to activate this partner? This will enable integration
						capabilities.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setActivateDialogOpen(false)} color="primary">
						Cancel
					</Button>
					<Button onClick={confirmActivatePartner} color="primary" autoFocus>
						Activate Partner
					</Button>
				</DialogActions>
			</Dialog>

			{/* Partner Deactivation Confirmation Dialog */}
			<Dialog
				open={deactivateDialogOpen}
				onClose={() => setDeactivateDialogOpen(false)}
				aria-labelledby="deactivate-dialog-title"
				aria-describedby="deactivate-dialog-description"
			>
				<DialogTitle id="deactivate-dialog-title">Deactivate Partner</DialogTitle>
				<DialogContent>
					<DialogContentText id="deactivate-dialog-description">
						Are you sure you want to deactivate this partner? This will disable all integration
						capabilities and remove all workplace assignments.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeactivateDialogOpen(false)}
						color="primary"
						className={styles.dialogBackButton}
					>
						Cancel
					</Button>
					<Button
						onClick={confirmDeactivatePartner}
						color="error"
						autoFocus
						className={styles.dialogBackButton}
					>
						Deactivate Partner
					</Button>
				</DialogActions>
			</Dialog>

			{/* Workplace Unassignment Confirmation Dialog */}
			<Dialog
				open={workplaceUnassignDialogOpen}
				onClose={() => {
					setWorkplaceUnassignDialogOpen(false);
					setPendingWorkplaceId(null);
				}}
				aria-labelledby="workplace-unassign-dialog-title"
				aria-describedby="workplace-unassign-dialog-description"
			>
				<DialogTitle id="workplace-unassign-dialog-title">Unassign Workplace</DialogTitle>
				<DialogContent>
					<DialogContentText id="workplace-unassign-dialog-description">
						Are you sure you want to unassign &quot;
						{workplaces.find((w) => w.id === pendingWorkplaceId)?.name}&quot; from this partner?
						This will remove the partner&apos;s access to this workplace.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setWorkplaceUnassignDialogOpen(false);
							setPendingWorkplaceId(null);
						}}
						color="primary"
						className={styles.dialogBackButton}
					>
						Cancel
					</Button>
					<Button
						onClick={confirmWorkplaceUnassign}
						color="warning"
						autoFocus
						className={styles.dialogBackButton}
					>
						Unassign Workplace
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default CreatePartnerSettings;
